import { Alert, Box, CircularProgress } from '@mui/material';
import { useFormikContext } from 'formik';
import { cloneDeep } from 'lodash';
import { Suspense, lazy, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CloseDialogResult, Dialog } from 'components';
import { usePrevious } from 'hooks';
import { TranslationNamespace } from 'i18n';

import { noOverlappingIntervals } from '../utils';

// FullCalendar (+timegrid/interaction plugins) adds ~75kB gzipped — split into its own chunk
// since this app ships one bundle with no route-level splitting, and the calendar is only
// needed once an admin actually opens this dialog.
const WorkingHoursCalendarPreview = lazy(() =>
  import('./WorkingHoursCalendarPreview').then((module) => ({
    default: module.WorkingHoursCalendarPreview,
  })),
);

type Props = {
  open: boolean;
  path: 'payinConfig' | 'payoutConfig';
  onClose: () => void;
};

export const WorkingHoursScheduleDialog: React.FC<Props> = ({
  open,
  path,
  onClose,
}) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_providers',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const formik = useFormikContext<any>();
  const prevOpen = usePrevious(open);
  const weekHoursSnapshot = useRef<any>(null);
  const weekHours = formik.values?.[path]?.workingHours?.weekHours;

  useEffect(() => {
    if (open && !prevOpen) {
      weekHoursSnapshot.current = cloneDeep(weekHours);
    }
  }, [open, prevOpen, weekHours]);

  // The outer provider form's Yup schema already rejects an overlap on submit, but that only
  // surfaces as the unrelated "Save provider" button going disabled with no visible reason.
  // Blocking OK here, with an inline error, catches it where the admin is actually looking.
  const hasOverlap = useMemo(
    () =>
      Object.values(weekHours || {}).some(
        (day: any) => !noOverlappingIntervals(day?.intervals),
      ),
    [weekHours],
  );

  const handleClose = ({ ok }: CloseDialogResult) => {
    if (!ok) {
      formik.setFieldValue(
        `${path}.workingHours.weekHours`,
        weekHoursSnapshot.current,
      );
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      title={t('fields.working_hours_title')}
      maxWidth="md"
      okDisabled={hasOverlap}
      onClose={handleClose}
    >
      {hasOverlap && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {tCommon('errors.working_hours_intervals_overlap')}
        </Alert>
      )}
      <Suspense
        fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        }
      >
        <WorkingHoursCalendarPreview path={path} weekHours={weekHours} />
      </Suspense>
    </Dialog>
  );
};
